import React, { useState } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { useDatabaseSwitchboardInstance } from '../../../App/mermaidData/databaseSwitchboard/DatabaseSwitchboardContext'
import { getSampleEventCounts } from '../../../library/getSampleEventCounts'
import { getToastArguments } from '../../../library/getToastArguments'
import { Column, FilterItems, ToolBarItemsRow } from '../../generic/positioning'
import { H2 } from '../../generic/text'
import { IconDownload } from '../../icons'
import ButtonSecondaryDropdown from '../../generic/ButtonSecondaryDropdown'
import { DropdownItemStyle } from '../../generic/ButtonSecondaryDropdown/ButtonSecondaryDropdown.styles'
import FilterIndicatorPill from '../../generic/FilterIndicatorPill/FilterIndicatorPill'
import FilterSearchToolbar from '../../FilterSearchToolbar/FilterSearchToolbar'
import MethodsFilterDropDown from '../../MethodsFilterDropDown/MethodsFilterDropDown'
import SuccessExportModal from '../../SuccessExportModal/SuccessExportModal'
import GatedFeature from '../../generic/GatedFeature'
import { SubmittedRecordForUiDisplay } from '../../../types/submitted'
import { Protocol } from '../../../types/protocols'

interface TableUserPrefsUpdate {
  propertyKey: string
  currentValue: string
}

interface SubmittedToolbarSectionProps {
  name: string
  globalSearchText?: string
  handleGlobalFilterChange: (value: string) => void
  handleMethodsColumnFilterChange: (value: string[]) => void
  methodFilterValue?: string[]
  disabled?: boolean
  unfilteredRowLength: number
  methodFilteredRowLength?: number | null
  searchFilteredRowLength?: number | null
  isSearchFilterEnabled?: boolean
  isMethodFilterEnabled?: boolean
  setMethodsFilter: (value: string[]) => void
  handleSetTableUserPrefs: (update: TableUserPrefsUpdate) => void
  submittedRecordsForUiDisplay: SubmittedRecordForUiDisplay[]
}

const SubmittedToolbarSection = ({
  name,
  globalSearchText,
  handleGlobalFilterChange,
  handleMethodsColumnFilterChange,
  methodFilterValue = [],
  disabled = false,
  unfilteredRowLength,
  methodFilteredRowLength = null,
  searchFilteredRowLength = null,
  isSearchFilterEnabled = false,
  isMethodFilterEnabled = false,
  setMethodsFilter,
  handleSetTableUserPrefs,
  submittedRecordsForUiDisplay,
}: SubmittedToolbarSectionProps) => {
  const { t } = useTranslation()
  const { databaseSwitchboardInstance } = useDatabaseSwitchboardInstance()
  const { projectId } = useParams<{ projectId: string }>()
  const [isSuccessExportModalOpen, setIsSuccessExportModalOpen] = useState(false)
  const [protocolSampleEventCount, setProtocolSampleEventCount] = useState(0)
  const sampleEventCounts = getSampleEventCounts(submittedRecordsForUiDisplay)

  const label = (
    <>
      <IconDownload /> {t('buttons.export_to_xlsx')}
    </>
  )

  const handleExportSubmitted = (protocol: Protocol) => {
    const sampleEventCount = sampleEventCounts[protocol] || 0
    setProtocolSampleEventCount(sampleEventCount)

    databaseSwitchboardInstance
      .exportSubmittedRecords({ projectId, protocol })
      .then(() => {
        setIsSuccessExportModalOpen(true)
      })
      .catch(() => {
        toast.error(...getToastArguments(t('toasts.export_error')))
      })
  }

  const clearFilters = () => {
    setMethodsFilter([])
    handleSetTableUserPrefs({ propertyKey: 'globalFilter', currentValue: '' })
    handleGlobalFilterChange('')
  }

  const closeModal = () => {
    setIsSuccessExportModalOpen(false)
    setProtocolSampleEventCount(0)
  }

  return (
    <>
      <H2>{t('sample_units.submitted')}</H2>
      <ToolBarItemsRow>
        <FilterItems>
          <FilterSearchToolbar
            name={name}
            disabled={disabled}
            globalSearchText={globalSearchText}
            handleGlobalFilterChange={handleGlobalFilterChange}
          />
          <MethodsFilterDropDown
            value={methodFilterValue}
            handleMethodsColumnFilterChange={handleMethodsColumnFilterChange}
            disabled={disabled}
          />
          {isSearchFilterEnabled || isMethodFilterEnabled ? (
            <FilterIndicatorPill
              unfilteredRowLength={unfilteredRowLength}
              methodFilteredRowLength={methodFilteredRowLength}
              searchFilteredRowLength={searchFilteredRowLength}
              isSearchFilterEnabled={isSearchFilterEnabled}
              isMethodFilterEnabled={isMethodFilterEnabled}
              clearFilters={clearFilters}
            />
          ) : null}
        </FilterItems>
        <ButtonSecondaryDropdown label={label} align="right">
          <Column as="nav" data-testid="export-to-csv">
            <DropdownItemStyle
              as="button"
              disabled={!sampleEventCounts?.fishbelt}
              onClick={() => handleExportSubmitted('fishbelt')}
            >
              {t('protocol_titles.fishbelt')}
            </DropdownItemStyle>
            <DropdownItemStyle
              as="button"
              disabled={!sampleEventCounts?.benthicpit}
              onClick={() => handleExportSubmitted('benthicpit')}
            >
              {t('protocol_titles.benthicpit')}
            </DropdownItemStyle>
            <DropdownItemStyle
              as="button"
              disabled={!sampleEventCounts?.benthiclit}
              onClick={() => handleExportSubmitted('benthiclit')}
            >
              {t('protocol_titles.benthiclit')}
            </DropdownItemStyle>
            <DropdownItemStyle
              as="button"
              disabled={!sampleEventCounts?.benthicpqt}
              onClick={() => handleExportSubmitted('benthicpqt')}
            >
              {t('protocol_titles.benthicpqt')}
            </DropdownItemStyle>
            <DropdownItemStyle
              as="button"
              disabled={!sampleEventCounts?.bleachingqc}
              onClick={() => handleExportSubmitted('bleachingqc')}
            >
              {t('protocol_titles.bleachingqc')}
            </DropdownItemStyle>
            <DropdownItemStyle
              as="button"
              disabled={!sampleEventCounts?.habitatcomplexity}
              onClick={() => handleExportSubmitted('habitatcomplexity')}
            >
              {t('protocol_titles.habitatcomplexity')}
            </DropdownItemStyle>
            <GatedFeature featureFlag="macroinvertebrate_enabled">
              <DropdownItemStyle
                as="button"
                disabled={!sampleEventCounts?.macroinvertebrate}
                onClick={() => handleExportSubmitted('macroinvertebrate')}
              >
                {t('protocol_titles.macroinvertebrate')}
              </DropdownItemStyle>
            </GatedFeature>
          </Column>
        </ButtonSecondaryDropdown>
      </ToolBarItemsRow>
      <SuccessExportModal
        isOpen={isSuccessExportModalOpen}
        onDismiss={closeModal}
        protocolSampleEventCount={protocolSampleEventCount}
      />
    </>
  )
}

export default SubmittedToolbarSection
